/**
 * Consent state + gated tag loading.
 *
 * NOTHING THAT SETS A NON-ESSENTIAL COOKIE MAY LOAD FROM MARKUP. Every tag is
 * injected by `applyConsent()` and only after the visitor has actually chosen.
 * That is the whole design: a pixel in the HTML head fires before any banner can
 * render, which makes the banner decorative and the policy false.
 *
 * That includes analytics, not just advertising. Microsoft Clarity used to sit
 * inline in index.html and fired for everyone; under ePrivacy an analytics
 * cookie is no more exempt than an ad cookie, so it moved in here behind the
 * `analytics` category.
 *
 * SHARED CONTRACT WITH THE ASTRO SITE. seo/src/layouts/Base.astro implements the
 * same thing for the SEO pages and MUST agree on:
 *   - the storage key            (CONSENT_KEY)
 *   - the stored value shape     (ConsentState)
 *   - the cookie name and domain (so a choice on the blog carries to the app)
 * Change one, change both, or a visitor gets asked twice.
 *
 * IDs come from build-time env vars, never from source. A tag with no ID
 * configured simply does not load — so this ships and does nothing until real
 * IDs exist, rather than shipping a placeholder that fails silently.
 */

export const CONSENT_KEY = 'fth_consent_v1'
/** Mirrored as a cookie so the Astro pages (different origin path, same site)
 *  read the same decision. Root path + apex domain, hence no Domain attribute
 *  in dev where it would break on localhost. */
export const CONSENT_COOKIE = 'fth_consent'
const COOKIE_MAX_AGE_DAYS = 180

export interface ConsentState {
  /** Product analytics — Microsoft Clarity. */
  analytics: boolean
  /** Advertising / retargeting — LinkedIn, Meta, Google Ads. */
  marketing: boolean
  /** ISO timestamp of the decision. Kept because "when did they agree, and to
   *  what version" is the only thing that makes consent auditable. */
  ts: string
}

export const CONSENT_ALL: Omit<ConsentState, 'ts'> = { analytics: true, marketing: true }
export const CONSENT_NONE: Omit<ConsentState, 'ts'> = { analytics: false, marketing: false }

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'))
  return m ? decodeURIComponent(m[1]) : null
}

/** The visitor's decision, or null if they haven't made one. */
export function getConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY) || readCookie(CONSENT_COOKIE)
    if (!raw) return null
    const v = JSON.parse(raw)
    if (typeof v?.analytics !== 'boolean' || typeof v?.marketing !== 'boolean') return null
    return v as ConsentState
  } catch {
    // Private mode, blocked storage, corrupt value — treat as undecided. Never
    // fall through to "assume yes".
    return null
  }
}

export function hasDecided(): boolean {
  return getConsent() !== null
}

export function setConsent(choice: Omit<ConsentState, 'ts'>): ConsentState {
  const state: ConsentState = { ...choice, ts: new Date().toISOString() }
  const encoded = JSON.stringify(state)
  try {
    localStorage.setItem(CONSENT_KEY, encoded)
  } catch { /* storage blocked — the cookie below still carries it */ }
  const secure = location.protocol === 'https:' ? '; Secure' : ''
  document.cookie =
    `${CONSENT_COOKIE}=${encodeURIComponent(encoded)}; Max-Age=${COOKIE_MAX_AGE_DAYS * 86400}` +
    `; Path=/; SameSite=Lax${secure}`
  applyConsent(state)
  return state
}

/** Forget the decision so the banner returns — the "change my mind" path a
 *  privacy policy has to be able to offer. Already-loaded tags are NOT unloaded;
 *  only a reload truly clears them, which is what the UI tells the user. */
export function resetConsent(): void {
  try { localStorage.removeItem(CONSENT_KEY) } catch { /* ignore */ }
  document.cookie = `${CONSENT_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`
}

// ── Tag loaders ────────────────────────────────────────────────────────────
// Each is idempotent and each no-ops without an ID, so re-running applyConsent
// (route change, re-render, second call) can never double-inject.

const loaded = new Set<string>()

function once(key: string, fn: () => void): void {
  if (loaded.has(key)) return
  loaded.add(key)
  fn()
}

function inject(src: string, attrs: Record<string, string> = {}): void {
  const s = document.createElement('script')
  s.async = true
  s.src = src
  for (const [k, v] of Object.entries(attrs)) s.setAttribute(k, v)
  document.head.appendChild(s)
}

function loadClarity(id: string): void {
  once('clarity', () => {
    ;(function (c: any, l: Document, a: string, r: string, i: string) {
      c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments) }
      const t = l.createElement(r) as HTMLScriptElement
      t.async = true
      t.src = 'https://www.clarity.ms/tag/' + i
      const y = l.getElementsByTagName(r)[0]
      y.parentNode!.insertBefore(t, y)
    })(window as any, document, 'clarity', 'script', id)
  })
}

function loadLinkedIn(partnerId: string): void {
  once('linkedin', () => {
    const w = window as any
    w._linkedin_partner_id = partnerId
    w._linkedin_data_partner_ids = w._linkedin_data_partner_ids || []
    w._linkedin_data_partner_ids.push(partnerId)
    inject('https://snap.licdn.com/li.lms-analytics/insight.min.js')
  })
}

function loadMeta(pixelId: string): void {
  once('meta', () => {
    const w = window as any
    if (!w.fbq) {
      const n: any = (w.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
      })
      if (!w._fbq) w._fbq = n
      n.push = n
      n.loaded = true
      n.version = '2.0'
      n.queue = []
      inject('https://connect.facebook.net/en_US/fbevents.js')
    }
    w.fbq('init', pixelId)
    w.fbq('track', 'PageView')
  })
}

function loadGoogleAds(tagId: string): void {
  once('google', () => {
    const w = window as any
    w.dataLayer = w.dataLayer || []
    w.gtag = w.gtag || function () { w.dataLayer.push(arguments) }
    inject(`https://www.googletagmanager.com/gtag/js?id=${tagId}`)
    w.gtag('js', new Date())
    w.gtag('config', tagId)
  })
}

/**
 * Load exactly the tags the visitor agreed to.
 *
 * Loading is one-way within a page: revoking after the fact cannot unload a
 * third-party script that has already run, so the UI tells the user a reload is
 * needed rather than pretending otherwise.
 */
export function applyConsent(state: ConsentState | null): void {
  const env = import.meta.env

  // ANALYTICS IS NOT GATED, and deliberately so.
  //
  // Clarity is first-party product analytics: it exists to answer whether
  // people can actually use this site. Gating it behind a banner meant it only
  // ever recorded visitors who stayed long enough to click Accept — which is
  // the exact inverse of the question, since the people worth seeing are the
  // ones who land and leave. It is disclosed in the privacy policy (section 9)
  // instead. Advertising tags below still require an explicit opt-in; they are
  // what a consent banner is actually for.
  //
  // Note this runs BEFORE the `!state` return: a visitor who has made no
  // choice still gets Clarity, and that is the point.
  const clarity = env.VITE_CLARITY_ID
  if (clarity) loadClarity(clarity)

  if (!state) return

  if (state.marketing) {
    const li = env.VITE_LINKEDIN_PARTNER_ID
    const meta = env.VITE_META_PIXEL_ID
    const ads = env.VITE_GOOGLE_ADS_ID
    if (li) loadLinkedIn(li)
    if (meta) loadMeta(meta)
    if (ads) loadGoogleAds(ads)
  }
}

/** Call once at boot: replays a prior decision without re-prompting. */
export function initConsent(): void {
  applyConsent(getConsent())
}

/** True when an ADVERTISING tag is configured — the only thing a banner is for.
 *
 *  Analytics is deliberately absent: Clarity loads for everyone (see
 *  applyConsent) and asking permission for it was what kept it from recording
 *  the visitors worth recording.
 *
 *  Nothing calls this today. The banner was removed along with the ad tags it
 *  gated, so `marketing` consent can never be granted and those loaders are
 *  inert until a banner comes back. Kept, with the loaders, so turning ads on
 *  is re-mounting a component rather than rebuilding the machinery. */
export function anyAdTagConfigured(): boolean {
  const env = import.meta.env
  return Boolean(
    env.VITE_LINKEDIN_PARTNER_ID ||
    env.VITE_META_PIXEL_ID || env.VITE_GOOGLE_ADS_ID,
  )
}
